import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, CheckCircle2, MapPin, Phone, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { companyInfo } from '../mock';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const About = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title={`${t('about.title')} - DumaExpertBéton`}
        description={t('about.whoText3')}
        url="https://dumaexpertbeton.fr/about"
      />
      
      {/* Header */}
      <div className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="flex justify-between items-start mb-4">
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all duration-200 cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">{t('common.back')}</span>
            </button>
            <LanguageSwitcher />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-4">{t('about.title')}</h1>
          <p className="text-xl text-slate-300 mt-4">{t('about.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Qui sommes-nous */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('about.whoAreWe')}</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                {t('about.whoText1')}
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                {t('about.whoText2')}
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                {t('about.whoText3')}
              </p>
            </div>
          </section>

          {/* Notre Expertise */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('about.ourExpertise')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="text-center border-slate-700 bg-slate-800">
                <CardContent className="pt-6">
                  <Award className="w-12 h-12 mx-auto mb-4 text-teal-400" />
                  <h3 className="text-xl font-bold text-white mb-2">{t('about.yearsTitle')}</h3>
                  <p className="text-slate-300">{t('about.yearsDesc')}</p>
                </CardContent>
              </Card>
              <Card className="text-center border-slate-700 bg-slate-800">
                <CardContent className="pt-6">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-teal-400" />
                  <h3 className="text-xl font-bold text-white mb-2">{t('about.projectsTitle')}</h3>
                  <p className="text-slate-300">{t('about.projectsDesc')}</p>
                </CardContent>
              </Card>
              <Card className="text-center border-slate-700 bg-slate-800">
                <CardContent className="pt-6">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-teal-400" />
                  <h3 className="text-xl font-bold text-white mb-2">{t('about.radiusTitle')}</h3>
                  <p className="text-slate-300">{t('about.radiusDesc')}</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Nos Valeurs */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('about.ourValues')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-slate-700 bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">{t('about.qualityTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{t('about.qualityDesc')}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-700 bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">{t('about.professionalismTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{t('about.professionalismDesc')}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-700 bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">{t('about.transparencyTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{t('about.transparencyDesc')}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-700 bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">{t('about.innovationTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{t('about.innovationDesc')}</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Contactez-nous */}
          <section className="bg-teal-500 text-white rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-6">{t('about.contactUs')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <Phone className="w-6 h-6" />
                <div>
                  <p className="font-semibold">{t('about.phoneLabel')}</p>
                  <p>{companyInfo.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6" />
                <div>
                  <p className="font-semibold">{t('about.emailLabel')}</p>
                  <p>{companyInfo.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-6 h-6" />
                <div>
                  <p className="font-semibold">{t('about.locationLabel')}</p>
                  <p>{companyInfo.address}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About;
